import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { AuthLayout } from "@/pages/Login";
import { Button } from "@/components/ui/Primitives";
import { usePageTitle } from "@/lib/seo";
import { Mail, ArrowLeft, CheckCircle2 } from "lucide-react";

/**
 * ForgotPassword — one-input form. Fires POST /auth/forgot and always shows
 * the same "check your inbox" success screen regardless of whether the email
 * exists (prevents account-enumeration).
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  usePageTitle("Reset your password — Central Rentals Canada");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api.post("/auth/forgot", { email: email.trim() });
      setSent(true);
    } catch (err) {
      // /auth/forgot always returns 200 on the server; any error here is
      // network-level, so surface a generic message.
      setError("Network error — please try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      title="Reset your password"
      subtitle="We'll email you a secure one-time link."
      accent="Password reset"
      accentClass="bg-emerald-50 text-emerald-800 border-emerald-200"
      PortalIcon={Mail}
    >
      {sent ? (
        <div className="text-center py-4" data-testid="forgot-success">
          <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mb-4">
            <CheckCircle2 className="w-7 h-7" />
          </div>
          <h3 className="font-display text-lg font-bold text-slate-900 mb-1">Check your inbox</h3>
          <p className="text-sm text-slate-600 leading-relaxed max-w-sm mx-auto">
            If an account exists for <strong>{email}</strong>, we&apos;ve sent a reset link.
            It expires in 30 minutes. Check your spam folder if it doesn&apos;t arrive within a couple of minutes.
          </p>
          <Link
            to="/login/landlord"
            data-testid="forgot-back-to-login"
            className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700 hover:underline"
          >
            <ArrowLeft className="w-4 h-4" /> Back to sign-in
          </Link>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4" data-testid="forgot-form">
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1.5 block">Email address</label>
            <input
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              data-testid="forgot-email-input"
              className="w-full bg-white border border-slate-200 text-slate-900 placeholder:text-slate-400 rounded-md px-3.5 py-3 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all shadow-sm"
            />
          </div>
          {error && (
            <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 px-3 py-2 rounded" data-testid="forgot-error">
              {error}
            </div>
          )}
          <Button
            type="submit"
            disabled={submitting || !email.trim()}
            className="w-full !bg-emerald-600 hover:!bg-emerald-700 !py-3"
            data-testid="forgot-submit"
          >
            {submitting ? "Sending…" : "Email me a reset link"}
          </Button>
          <div className="text-center pt-2">
            <Link
              to="/login/landlord"
              className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
              data-testid="forgot-cancel-link"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to sign-in
            </Link>
          </div>
        </form>
      )}
    </AuthLayout>
  );
}
